import { CopyElement } from "@ui";
import style from "./Input.module.scss";
import cn from "classnames";
import { useState } from "react";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { RiLoader5Fill } from "react-icons/ri";

export function Input({
  name,
  value,
  onChange,
  onBlur,
  label,
  type = "text",
  onSearch,
  customStyle,
  actions,
  error,
  readOnly,
  onClick
}: {
  name?: string
  value: string;
  onChange?: (newValue: string) => void;
  onClick?: () => void,
  onBlur?: (event: React.FocusEvent<HTMLInputElement, Element>) => void
  label?: {
    text?: string,
    bg?: string
  } | string;
  type?: "text" | "password" | "search" | "tel" | "email";
  onSearch?: (currentValue: string) => void;
  customStyle?: {
    width?: React.HTMLAttributes<HTMLElement>["className"];
    height?: React.HTMLAttributes<HTMLElement>["className"];
  };
  actions?: "copy" | "load",
  error?: string,
  readOnly?: boolean
}) {
  const [isVisiblePass, setIsVisiblePass] = useState<boolean>(false);

  return (
    <div className={cn("max-w-[200px] grow", customStyle?.width)}>
      <label
        className={cn(
          style.container,
          style["type_" + type],
          actions && style.actions_show,
          customStyle?.height,
        )}
      >
        <input
          type={
            (() => {
              if (type === "password") {
                return type !== "password" ? "text" : isVisiblePass ? "text" : "password"
              } else if (type === "search") {
                return "text"
              } else {
                return type;
              }
            })()
          }
          onClick={onClick}
          name={name}
          className={style.input}
          value={value}
          onChange={(e) => {
            if (type === "tel") {
              onChange?.(formatPhoneNumber(e.target.value))
            } else {
              onChange?.(e.target.value)
            }
          }}
          readOnly={readOnly}
          onBlur={onBlur}
        />
        <div className={cn(style.label, value.length && style.label_focus)}>
          <p className={cn("w-fit px-[2px]", ((typeof label === "object") && label?.bg) ? `bg-[${label.bg}]` : "bg-white")}>
            {typeof label === "string" ? label : label?.text}
          </p>
        </div>
        <div className={cn(
          style.actions,
          actions && "!flex"
        )}>
          <VisiblePassButton
            isVisiblePass={isVisiblePass}
            setIsVisiblePass={(status) => setIsVisiblePass(status)}
          />
          <SearchButton
            onClick={() => {
              if (value && onSearch) {
                onSearch(value);
              }
            }}
          />
          {
            actions === "copy"
              ? <CopyElement
                copyText={value}
                element={<></>}
              />
              : null
          }
          {
            actions === "load"
              ? <RiLoader5Fill className={"animate-spin"} />
              : null
          }
        </div>
      </label>
      {
        error
          ? <p className={style.error}>{error}</p>
          : null
      }
    </div>
  );
}

function VisiblePassButton({
  isVisiblePass,
  setIsVisiblePass,
}: {
  isVisiblePass: boolean;
  setIsVisiblePass: (status: boolean) => void;
}) {
  return (
    <button type="button" className={style.visiblePassButton}>
      {isVisiblePass ? (
        <IoEyeOutline
          className={style.icon}
          onClick={() => setIsVisiblePass(false)}
        />
      ) : (
        <IoEyeOffOutline
          className={style.icon}
          onClick={() => setIsVisiblePass(true)}
        />
      )}
    </button>
  );
}

function SearchButton({ onClick }: { onClick: () => void }) {
  return (
    <button type="button" className={style.searchButton} onClick={onClick}>
      <svg
        className={style.icon}
        xmlns="http://www.w3.org/2000/svg"
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
      >
        <path
          d="M17.5 17.5L14.5833 14.5833M14.1667 8.33333C14.1667 9.09938 14.0158 9.85792 13.7226 10.5657C13.4295 11.2734 12.9998 11.9164 12.4581 12.4581C11.9164 12.9998 11.2734 13.4295 10.5657 13.7226C9.85792 14.0158 9.09938 14.1667 8.33333 14.1667C7.56729 14.1667 6.80875 14.0158 6.10101 13.7226C5.39328 13.4295 4.75022 12.9998 4.20854 12.4581C3.66687 11.9164 3.23719 11.2734 2.94404 10.5657C2.65088 9.85792 2.5 9.09938 2.5 8.33333C2.5 6.78624 3.11458 5.30251 4.20854 4.20854C5.30251 3.11458 6.78624 2.5 8.33333 2.5C9.88043 2.5 11.3642 3.11458 12.4581 4.20854C13.5521 5.30251 14.1667 6.78624 14.1667 8.33333Z"
          stroke="#A2AFD0"
          strokeWidth="1.6"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
}

const formatPhoneNumber = (value: string) => {
  const numericValue = value.replace(/\D/g, "");

  let formattedValue = "";

  if (numericValue.length > 0) {
    formattedValue = "+" + numericValue.slice(0, 2);
  }
  if (numericValue.length > 2) {
    formattedValue += " (" + numericValue.slice(2, 5);
  }
  if (numericValue.length > 5) {
    formattedValue += ") " + numericValue.slice(5, 8);
  }
  if (numericValue.length > 8) {
    formattedValue += numericValue.slice(8, 13);
  }

  return formattedValue.slice(0, 18);
};